import React, { useCallback, useState } from 'react';
import { uploadOrderFiles, confirmRemoveAttachment, deleteOrderFile, refreshOrderFiles, isItemAttachment } from '../utils/orderFileMutations';
import ApprovalStatus from './ApprovalStatus';
import OrderAttachments from './OrderAttachments';
import OrderActivityHistory from './OrderActivityHistory';
import { DESKTOP_SECTION_CARD, DESKTOP_SECTION_TITLE } from './OrderSectionHeader';
import useUploadProgress from '../hooks/useUploadProgress';
import { orderItemFileLabel } from '../utils/orderPdf';

// 🖥️ The three lower sections as their own desktop cards (owner 2026-10-10: each
// section "in a similar style to the next"); the phone keeps its divider rows.
const LOWER_SECTION_CLASS = `border-t border-gray-200 dark:border-gray-700 pt-2 md:border-t-0 ${DESKTOP_SECTION_CARD}`;

/**
 * The three sections that closed the gap between the expanded order row and the
 * full order page (2026-08-10): ATTACHMENTS (order files + delivery paperwork),
 * the APPROVAL STEPPER, and ACTIVITY HISTORY.
 *
 * Product owner: *"expanding an order still doesnt show full detials including
 * history? Once we have ALL details on the expanded sections we can loose the
 * dedicated indvidule details pages for each order"*.
 *
 * Rendered by BOTH branches of the expanded panel on BOTH order-table pages
 * (`pages/DepartmentOrders.js`, `pages/Approvals.js`) — the read-only panel and
 * the inline-editor panel — because none of these three has any counterpart
 * inside `OrderForm`, so unlike PO number / job / notes / line items there is
 * nothing here for the form to supersede and no risk of two controls that can
 * disagree. Both pages build the node; `components/OrderTableView.js` still
 * receives a finished node and decides nothing.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 REQUESTS FIRE ON EXPAND, NEVER ON RENDER — AND IT IS STRUCTURAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Department Orders and the Approvals TABLE are ACCORDIONS (`toggleExpand` sets
 * `new Set([orderId])`), and `OrderTableRow` mounts `expandedContent` only
 * inside `{isExpanded && …}`, and only once `detail` has resolved
 * (`components/OrderTableView.js:1611-1631`). Building the element is free —
 * `React.createElement` does not run a component — so a list of 200 collapsed
 * rows constructs 200 of these nodes and mounts none.
 *
 * Per EXPAND gesture this component therefore costs, at most:
 *   • 1 × `GET /api/approvals/orders/:id`  — `ApprovalStatus`, on mount
 *   • 0 × `GET /api/orders/:id/audit-log`  — LAZY, see below
 * and on RENDER, zero of either.
 *
 * 🔑 THE HISTORY IS BETTER THAN THE ONE-PER-EXPAND THE BRIEF ALLOWED FOR: it is
 * collapsed by default AND fetches only on first open, memoised thereafter. So
 * an expand that nobody drills into costs ONE request in total, and reopening
 * the same row's history costs none.
 *
 * ⚠️ **THIS IS THE PROPERTY TO PROTECT AND IT IS ONE EDIT AWAY FROM GONE.** Any
 * of these turns the panel into the client-side N+1 CLAUDE.md names for this
 * surface — where `server/tests/api/n-plus-1-sweep.test.js` cannot see it,
 * because the requests come from the browser:
 *   • pre-fetching either endpoint in the page's `fetchOrders`;
 *   • moving any of this into the COLLAPSED row (the item preview's rule:
 *     `orderPreviewItems` "never issues a request");
 *   • rendering this outside the `isExpanded` branch to "avoid a remount";
 *   • making the history eager to "make it feel faster".
 * Pinned by `OrderExpandedDetails.requests.test.js`, which counts real fetch
 * calls across a render, an expand and a collapse.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 PERMISSIONS — THIS COMPONENT ASKS NO AUTHORITY QUESTION OF ITS OWN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every gate is resolved by the PAGE and passed in, beside the page's other
 * gates, so all of them stay auditable in one place and none is re-derived per
 * layout. What each section's SERVER gate actually is — read from the routes,
 * not assumed, because the four differ and two of them are NOT
 * `canManageDepartmentOrders`:
 *
 * ⚠️ ALL FOUR `routes/orders.js` CITATIONS BELOW WERE RE-DERIVED BY READING
 * ON 2026-08-12 (`fix/delete-uncancel`) AND EVERY ONE WAS BADLY WRONG — not
 * drifted, wrong. They read `:2875` / `:2889` / `:2955` / `:2980-2998`; the
 * real anchors are `:3309` / `:3323` / `:3264` / `:3414-3432`, i.e. they were
 * roughly **420 lines short**. That staleness PREDATES this branch — proven
 * by measuring the same anchors on the merge base, where they were `:3322` /
 * `:3261` / `:3417` — so nothing recent caused it and no arithmetic on a
 * recent diff would have found it. Eleven citations in five client files all
 * pointed into the same wrong region, which is what a number copied between
 * files rather than read looks like.
 *
 * 🔑 THE ONLY RELIABLE MOVE IS TO GREP FOR THE ANCHOR TEXT: `router.post(
 * '/:id/files'`, `category === 'delivery'`, `router.delete('/:id/files/
 * :fileId'`, `router.post('/:id/items/:itemId/files'`. Treat every `:NNNN`
 * here as a hint, never as a fact, and re-read before relying on one.
 *
 *  • LIST + DOWNLOAD (both kinds) — `GET /api/orders/:id` already carries
 *    `files`, gated by `canViewOrder`. Anyone who can see this panel may see
 *    the list, so there is no additional client gate and must not be one.
 *    Downloads go to `file.file_path` = `/uploads/…`, the AUTHENTICATED router
 *    (`server/routes/uploads.js`), which re-runs the same gate.
 *
 *  • ATTACH an order-level file — `POST /api/orders/:id/files` with no
 *    `?category`, gated on `canViewOrder` ALONE (`server/routes/orders.js:3309`).
 *    So it is offered to everyone who can open the panel. That is the server's
 *    policy, mirrored; narrowing it here would hide a control the server allows.
 *
 *  • ATTACH DELIVERY PAPERWORK — `POST /api/orders/:id/files?category=delivery`,
 *    gated on `isGlobalAdmin || canManageDepartmentOrders(order's OWN dept) ||
 *    isOwner || isStores` (`server/routes/orders.js` — MIGRATED off the
 *    any-department `req.isBuyer` in tranche 3 increment 3d, 2026-08-14). 🔑 It
 *    is now the SAME server predicate as the received gate below, so the PAGE
 *    resolves ONE value for both: `isAdmin || useOrderAuthority().canManageOrder
 *    (order)` (canManageDepartmentOrders in the order's OWN department) `||
 *    isOwner || isStores`. This ADMITS the correctly-created department buyer
 *    (no unscoped `user_roles` row) and the supervisor that the pre-3d
 *    `currentRole === 'buyer'` form under-served — matching what the server now
 *    allows. 🟢 A `sub_department_lead` satisfies neither term, so nothing leaks.
 *
 *  • REMOVE a file — `DELETE /api/orders/:id/files/:fileId`, which decides PER
 *    FILE (`server/routes/orders.js:3414-3432`): a global admin or in-department
 *    buyer may remove any; the file's own UPLOADER may remove theirs; and a
 *    legacy row with `uploaded_by_user_id IS NULL` falls back to `canEditOrder`.
 *    Hence `canRemoveFile` is a per-file predicate, not a boolean.
 *
 * The STEPPER and the HISTORY are read-only and each endpoint applies its own
 * gate (`canViewOrder` for the audit log), so neither takes a client gate.
 * 🔑 The stepper renders with `showActions={false}` — see `ApprovalStatus.js`'s
 * docblock: its three controls each end in `window.location.reload()`, which in
 * an expanded row discards the row and any unsaved edit.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 📦 ITEMS RECEIVED — MOVED OUT OF THIS PANEL (2026-08-15)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The standalone "Items received" block (per-item checkboxes + tick-all,
 * 2026-08-14, between Attachments and Delivery Paperwork) is RETIRED. Product
 * owner: *"I would like the tick boxes to go in the collpased line items in
 * the 'Line items' section … This will save the whole Items received
 * section."* The ticks now live IN the line-item rows of every surface that
 * lists items — `OrderForm`'s collapsed item headers (via its optional
 * `received` prop) and the read-only item tables/cards `DepartmentOrders.js`
 * and `MyOrders.js` build themselves — with the tick-all + the
 * "Line Items — received (x/N)" count on each surface's own section header.
 * See `components/OrderItemReceivedControls.js` (the shared controls) and
 * `utils/receivedItems.js` (the shared mutations; same endpoints, same
 * detail-then-list refresh sequence this block used). 🔴 Do not "restore" the
 * section here — two surfaces ticking one fact is the disagreement the
 * expanded-panel design exists to prevent. This component no longer takes
 * `canReceiveItems`/`onItemsReceived` and issues no received PATCH.
 */
function OrderExpandedDetails({
  // The DETAIL payload (`GET /api/orders/:id`), never the list row — it is what
  // carries `files`, and its `can_edit` is the fresh one.
  order,
  // Server gate mirrored (since increment 3d, identical to the received gate
  // the line-item ticks use): isGlobalAdmin || canManageDepartmentOrders
  // (order's dept) || isOwner || isStores.
  canUploadDelivery = false,
  // Per-file predicate mirroring the DELETE route. Default refuses, so a caller
  // that forgets it shows no Remove rather than a 403 button.
  canRemoveFile = () => false,
  // Called after any successful upload/delete so the page can refresh its
  // detail cache (the file list lives on the detail payload).
  onFilesChanged = null,
  // ── THE SAVE-EPOCH GUARD (2026-09-13, review F1) ────────────────────────
  // OPTIONAL. A ref whose `.current` the INLINE EDITOR bumps each time a save
  // lands. Captured before this panel's own detail re-fetch and re-read after
  // it, so a GET issued BEFORE a `PUT /api/orders/:id` cannot land afterwards
  // and overwrite the page's freshly-saved cache with pre-PUT data. The full
  // sequence is at `utils/orderFileMutations.js`'s `refreshOrderFiles`.
  //
  // 🔑 It is OPTIONAL because this panel renders in BOTH branches and the
  // read-only one has no save to race with: absent, the guard is inert and this
  // component behaves exactly as it did before the prop existed. Only
  // `components/OrderExpandedRow.js` supplies it, and only because it is the
  // one component that has both this panel and the editor as children.
  saveEpochRef = null,
  testIdPrefix = '',
  // md+: the order form's own 24px between section cards (2026-10-10).
  className = 'mt-3 space-y-3 md:mt-6 md:space-y-6',
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // Per-file bars for the two drop zones (2026-09-14) — see `UploadingFileRow`.
  const { begin: beginUpload, dismiss: dismissUpload, entriesIn: uploadsIn } = useUploadProgress();

  const orderId = order?.id;
  const files = Array.isArray(order?.files) ? order.files : [];
  const items = Array.isArray(order?.items) ? order.items : [];

  // ═══════════════════════════════════════════════════════════════════════
  // THE THREE-WAY SPLIT OF `order.files` — EXHAUSTIVE AND DISJOINT
  // ═══════════════════════════════════════════════════════════════════════
  //
  // 🔴 UNTIL 2026-08-11 THIS WAS A TWO-WAY SPLIT AND IT DROPPED A WHOLE
  // CATEGORY ON THE FLOOR. It read:
  //
  //     attachmentFiles = files.filter(f => !f.order_item_id && f.category !== 'delivery')
  //     deliveryFiles   = files.filter(f => f.category === 'delivery')
  //
  // — so order-level files rendered, delivery paperwork rendered, and anything
  // attached to a LINE ITEM rendered NOWHERE in the panel. A client's buyer
  // reported it: *"Paul Dunn attached a quotation to an order and I can't see it
  // on my end"*. The files were never lost — `pages/OrderPage.js:737` partitions
  // per item and shows them — but this panel is the surface replacing that page,
  // so "it is visible somewhere else" stops being an answer.
  //
  // 🔑 THE TWO PREDICATES ARE NAMED AND THE THREE LISTS ARE BUILT FROM THEM, so
  // the split is exhaustive and disjoint BY CONSTRUCTION rather than by three
  // filter expressions that happen to agree today. Every file lands in exactly
  // one list; adding a fourth category means editing one predicate, not
  // auditing three conditions for overlap.
  //
  // ⚠️ DELIVERY WINS THE OVERLAP, and the overlap is UNREACHABLE — checked in
  // the routes rather than assumed, because it is a property of two INSERT
  // statements somebody will edit:
  //   • `POST /:id/items/:itemId/files` (`server/routes/orders.js:3264`) omits
  //     `category` from its column list entirely, so the row takes the DB
  //     default `'attachment'` (`database.js:690`, `database.postgres.js:606`);
  //   • `POST /:id/files` (`:3027`) hard-codes `order_item_id = null`.
  //   • `helpers/departmentBackup.js:1695` replays exported rows through a
  //     generic `insertRows` — it cannot mint a combination that never existed.
  // So no live path can produce a file that is both. The ordering here is what
  // keeps a future one from being counted twice rather than a live concern.
  //
  // 🪤 `!= null`, NOT `!f.order_item_id`. The falsy form was the shipped one and
  // it is the house rule this file's own CLAUDE.md states — *"a falsy value that
  // is also a legitimate answer can never double as the absence marker"*. No id
  // is 0 on either engine, so nothing was broken by it; what it cost is that the
  // two surfaces splitting this same array disagreed on the test, and
  // `utils/orderPdf.js:139` was already using `== null`. One question, one
  // answer.
  // 🔀 `isItemAttachment` moved to `utils/orderFileMutations.js` on 2026-09-13 —
  // the edit branch's new per-item control asks the same question, and two
  // answers to it is exactly what the `!= null` note below is about.
  const isDelivery = (f) => f.category === 'delivery';
  const isItemFile = (f) => f.order_item_id != null;
  const deliveryFiles = files.filter(isDelivery);
  const attachmentFiles = files.filter(f => !isDelivery(f) && !isItemFile(f));
  const itemAttachmentFiles = files.filter(isItemAttachment);

  // 🔴 ONE LIST, WITH THE LINE NAMED — **not** grouped under each line item, and
  // the argument that decided it is structural rather than aesthetic.
  //
  // The scanning argument ("a buyer wants *here are the attachments on this
  // order*, not a hunt through lines") is real but it is not what settles it.
  // What settles it is that this panel has TWO branches and grouping only works
  // in one:
  //   • the READ-ONLY branch's line-items block is not owned here at all — it is
  //     built independently in `pages/DepartmentOrders.js` and
  //     `pages/Approvals.js`, each in a mobile-card AND a desktop-table layout.
  //     Four render sites, none of them in this shared component.
  //   • the EDIT branch renders items through `OrderForm`, which until
  //     2026-09-13 had `showItemFileUpload` false there.
  // A design that requires contradicting that is the wrong design. The flat list
  // is ONE site and behaves identically in both branches — which stops being a
  // convenience and becomes the requirement once `/orders/:id` is retired.
  //
  // 🔀 **THE EDIT BRANCH NOW TURNS `showItemFileUpload` ON (2026-09-13), AND THE
  // PARAGRAPH THAT SAID IT COULD NOT IS HISTORY — BUT THE POLICY IT PROTECTED IS
  // KEPT, NOT BREACHED.** Owner, asked whether per-item attachment had to exist
  // before `/orders/:id` retires: *"Yes attachments per item is needed"*. The old
  // reasoning was *"`OrderForm` STAGES per-item files until submit and
  // `pages/OrderPage.js` flushes them after its PUT; `OrderInlineEditor` has no
  // flush, so they would be stranded"*. **The answer was not to build a flush —
  // it was to stop staging.** `OrderInlineEditor` passes `itemFilesImmediate`, so
  // a pick POSTs straight to `POST /api/orders/:id/items/:itemId/files` and the
  // panel re-reads the detail, exactly as the drop zone below already does. So
  // "UPLOAD IS IMMEDIATE, NOT STAGED-UNTIL-SAVE" now governs BOTH branches
  // rather than being the reason one of them had no control.
  // 🔴 **THIS IS STILL THE ONE LIST.** The edit branch's per-item control is an
  // INPUT affordance; the files it creates appear HERE, in this flat list, under
  // `orderItemFileLabel`'s wording, the same as any other item file. It is not a
  // second list and must not grow into one.
  // 🔀 **A LINE ITEM ADDED DURING THE EDIT — REVERSED BY THE OWNER 2026-09-14.**
  // This said its control was DISABLED and that a staged flush was the rejected
  // design. Owner: *"I think it should be allowed to upload a file before the
  // new line item is saved."* Such a line STAGED its files until 2026-09-15; since
  // R13 (owner: *"upload the file straight away, delete it if the user does not
  // save the line or they delete the line"*) they UPLOAD AT PICK to the pending
  // store and `OrderInlineEditor`'s `putOrder` CLAIMS them after the PUT and
  // BEFORE the row collapses — a refused claim keeps the row open and names the
  // file — by the line's echoed `client_key`, never by position. Saved lines
  // still upload immediately, and the result still lands in THIS one list.
  //
  // 🔑 THE LABEL IS A REUSE. `orderItemFileLabel` is `utils/orderPdf.js`'s own
  // already-shipped format for this exact attribution problem, extracted rather
  // than re-invented so the panel and the PDF cannot drift.
  //
  // 🪤 FAILS OPEN ON AN UNKNOWN ITEM. A file whose `order_item_id` matches no row
  // in `order.items` still renders, unlabelled. Hiding it would reinstate the
  // very defect this closes; losing the attribution merely costs a chip. The
  // payload can genuinely be in that state — `files` and `items` are two
  // independent queries in `GET /api/orders/:id` and only `items` is filtered.
  const itemIndexById = new Map(items.map((it, idx) => [it.id, idx]));
  const orderFileLabel = (f) => {
    if (!isItemFile(f)) return null;
    const idx = itemIndexById.get(f.order_item_id);
    return idx == null ? null : orderItemFileLabel(items[idx], idx);
  };

  // Order-level files FIRST, then the per-item ones. 🔑 Not sorted, CONCATENATED:
  // the order-level block keeps the exact sequence it rendered before this
  // change, so nothing an existing user is used to moves, and the new rows
  // arrive underneath it in item order.
  const allAttachmentFiles = attachmentFiles.concat(
    itemAttachmentFiles.slice().sort((a, b) => {
      const ai = itemIndexById.has(a.order_item_id) ? itemIndexById.get(a.order_item_id) : Number.MAX_SAFE_INTEGER;
      const bi = itemIndexById.has(b.order_item_id) ? itemIndexById.get(b.order_item_id) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    })
  );

  const tid = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix);

  // Both mutations refresh through the PAGE rather than holding their own copy
  // of the order: the panel's file list is a slice of the detail cache, and a
  // second source of truth for it is how the cached `can_edit` went stale in
  // the first place (see `components/OrderInlineEditor.js`'s entry in CLAUDE.md).
  // 🔀 THE REQUEST ITSELF MOVED TO `utils/orderFileMutations.js` ON 2026-09-13 —
  // this component keeps the decision (refresh through the PAGE) and hands the
  // transport to the shared helper, because `components/OrderInlineEditor.js`'s
  // new per-item attach control needs the identical three requests. One upload
  // path, one delete path, one re-fetch — see that file's docblock.
  const refresh = useCallback(
    () => refreshOrderFiles(orderId, onFilesChanged, { saveEpochRef }),
    [onFilesChanged, orderId, saveEpochRef]
  );

  // 🔑 UPLOAD IS IMMEDIATE, NOT STAGED-UNTIL-SAVE, and that is a decision —
  // FOR THIS PANEL'S ORDER-LEVEL FILES. `pages/OrderPage.js`'s EDIT mode uploads
  // to the pending store and claims after its PUT (R13, 2026-09-15); the panel
  // does not: the read-only branch has no save at all to claim on. Immediate upload also
  // means an attachment survives "Discard changes", which is what a user expects
  // of a delivery note they just attached.
  // 🔀 2026-09-14: reason (2) that stood here — "a flush through
  // `saveRequestRef` adds a second failure mode to a structural promise" — was
  // overruled for LINE files on UNSAVED lines only (see the reversal above).
  // The promise survives in a sharper form: a failed flush resolves the save
  // request `false`, so the page's action still cannot run on it.
  const upload = useCallback(async (fileList, category) => {
    if (!orderId || !fileList || fileList.length === 0) return;
    setBusy(true);
    setMessage(null);
    const tracker = beginUpload(fileList, category);
    try {
      const uploaded = await uploadOrderFiles(orderId, fileList, { category, onProgress: tracker.onProgress });
      // Refresh FIRST, so the listed rows replace the bars rather than a gap.
      await refresh();
      tracker.succeed();
      setMessage({ type: 'success', text: `${uploaded.length} file(s) uploaded` });
    } catch (err) {
      tracker.fail(err.message);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  }, [orderId, refresh, beginUpload]);

  const removeFile = useCallback(async (fileId) => {
    if (!orderId) return;
    // 🪤 **ASK BEFORE TOUCHING ANY STATE — review F4, 2026-09-13.** The
    // extraction briefly set `busy` and cleared `message` ahead of the confirm,
    // so DECLINING wiped the banner from the previous upload or removal. A
    // question the user answers "no" to must leave the panel exactly as it was.
    if (!confirmRemoveAttachment()) return;
    setBusy(true);
    setMessage(null);
    try {
      await deleteOrderFile(orderId, fileId);
      await refresh();
      setMessage({ type: 'success', text: 'Attachment removed' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  }, [orderId, refresh]);

  if (!order || !orderId) return null;

  return (
    <div className={className} data-testid={tid('expanded-details')}>
      {message && (
        <div
          className={`px-3 py-2 rounded text-sm ${message.type === 'success'
            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}
          data-testid={tid('attachment-message')}
        >
          {message.text}
        </div>
      )}

      {/* ===== Order attachments — ORDER-LEVEL **AND** LINE-ITEM FILES, in one
          list, each item file naming its line. Server gate for attaching is
          `canViewOrder` alone, so the drop zone is offered to everyone who can
          open this.
          🔴 THE DROP ZONE STILL ATTACHES AT THE ORDER LEVEL, and that is not an
          oversight: `upload()` posts to `POST /api/orders/:id/files`, which
          hard-codes `order_item_id = null`. Listing item files here does not
          make this a place to CREATE them — the per-item route needs an item to
          aim at, and this panel has no per-item control to aim with. 🔀 The
          control that DOES aim is `OrderForm`'s per-item one, turned on in the
          EDIT branch since 2026-09-13 (see the docblock); the full order page is
          no longer the only route to it.
          Read-then-write asymmetry, stated rather than left to be discovered. */}
      <OrderAttachments
        title="Attachments"
        phoneTitle="Attachments"
        existingFiles={allAttachmentFiles}
        fileLabel={orderFileLabel}
        onFilesChange={busy ? null : (next) => upload(next, 'attachment')}
        uploadingEntries={uploadsIn('attachment')}
        onDismissUpload={dismissUpload}
        onExistingFileToggleRemove={removeFile}
        canRemoveFile={canRemoveFile}
        testIdPrefix={tid('order-files')}
        emptyText="No attachments yet"
        className={LOWER_SECTION_CLASS}
        desktopSectionHead
      />

      {/* ===== "Items received" RENDERED HERE from 2026-08-14 to 2026-08-15
          and is RETIRED — the ticks live in the line-item rows now (the
          owner's "save the whole Items received section"). See the MOVED
          section of the docblock above; do not restore a second surface. */}

      {/* ===== Delivery paperwork — the same `detail.files` array filtered on
          category, so it costs NO extra request. This is what replaces the
          "Upload Paperwork" button that navigated to /orders/:id?upload=1.
          🔴 Everyone who can see the panel sees the LIST; only a caller
          satisfying the server's delivery gate gets the drop zone. */}
      <OrderAttachments
        title="Delivery Paperwork"
        phoneTitle="Delivery paperwork"
        existingFiles={deliveryFiles}
        onFilesChange={(!canUploadDelivery || busy) ? null : (next) => upload(next, 'delivery')}
        uploadingEntries={uploadsIn('delivery')}
        onDismissUpload={dismissUpload}
        onExistingFileToggleRemove={removeFile}
        canRemoveFile={canRemoveFile}
        testIdPrefix={tid('delivery-files')}
        emptyText="No delivery paperwork uploaded yet"
        className={LOWER_SECTION_CLASS}
        desktopSectionHead
      />

      {/* ===== Activity history — COLLAPSED by default and lazily fetched.
          🎨 `headerVariant="inline"` (2026-08-10) is what makes this section's
          collapsed header the SAME control as the two above it: chevron first at
          `h-3.5 w-3.5` with `data-chevron`, no card chrome, no `data-section-title`.
          The default `card` variant is the full order page's, whose negative
          margins cancel a `p-2.5` this container does not have. The reference is
          `components/OrderAttachments.js`'s header — the two renders above — so
          if that changes, this follows it. Reasoning at HEADER_VARIANTS.
          🔑 A STYLING PROP ONLY: `defaultOpen={false}` is untouched, so the
          history is still collapsed on expand and still fetches lazily on first
          open. `OrderExpandedDetails.requests.test.js` counts those calls.
          🔻 `persistOpenState={false}` (2026-09-12, owner: *"on expanded orders
          'Activity History' should always be collapsed"*) is what makes
          `defaultOpen={false}` actually bind HERE. The two surfaces shared one
          localStorage key and a stored value BEATS `defaultOpen`, so one expand
          anywhere — including on the full order page — opened the unbounded log
          in every row from then on. This row now neither reads nor writes that
          key; the full page still does. Pinned by
          `OrderActivityHistory.persist.test.js`.
          🔑 `space-y-2`, NOT `space-y-3` — MEASURED, not guessed. The two
          OrderAttachments sections put `mt-2` on their body, so their
          header→body gap is 8px; this container's `space-y-*` is what supplies
          the same gap here, and at `space-y-3` it measured 12px against their 8
          in Chromium at 1280. Collapsed there is one child, so it changes
          nothing there — this is the OPEN half of "consistent with the two
          above". The 12px BETWEEN the three sections comes from this
          component's own root `space-y-3` and was already uniform. */}
      <OrderActivityHistory
        orderId={orderId}
        defaultOpen={false}
        persistOpenState={false}
        headerVariant="inline"
        testIdPrefix={tid('history')}
        className={`${LOWER_SECTION_CLASS} space-y-2`}
        headingClassName={`text-sm font-medium text-gray-600 dark:text-gray-400 ${DESKTOP_SECTION_TITLE}`}
      />

      {/* ===== Approval stepper — READ-ONLY, and LAST since 2026-08-11.
          Product owner: *"i feel like approval workflow should be below activity
          history?"*. It rendered FIRST from 2026-08-10 until then.

          🔑 PRESENTATION ONLY, AND THAT WAS VERIFIED RATHER THAN ASSUMED. Three
          things could have been positional and none is:
           • the stepper's own fetch — it is a mount effect, and React commits
             every child in one pass whatever their source order, so it still
             fires exactly once per expand;
           • the history's laziness — it comes from its own `defaultOpen={false}`
             and its first-open memo, not from what sits below it;
           • the section headers' agreement — `sectionHeaders.test.js` addresses
             all three disclosures by `data-testid`, so none of its claims was
             about position.
          Pinned by `OrderExpandedDetails.sectionOrder.test.js`, which compares
          `compareDocumentPosition` rather than presence — every OTHER test over
          this component stays green under any permutation of the four sections,
          which is exactly how an order can be reverted by accident in silence.

          🪤 IT IS THE ONE SECTION WITH NO `border-t`, so moving it made
          Attachments the first child and therefore put a hairline across the TOP
          of the panel. That state was already reachable before this change —
          `ApprovalStatus` returns null while loading and on `!has_workflow`
          (`ApprovalStatus.js:255-256`), so any order without a workflow already
          rendered that way — and it reads as a divider from the quick-actions
          row above, which is what it is. Checked in Chromium at 375/1024/1280/
          1440 in both themes. Do not "fix" it by adding a `border-t` here: the
          stepper carries a full card border of its own and would then have two.

          See ApprovalStatus.js's docblock for why all three of its controls are
          suppressed rather than just the two that duplicate. */}
      <ApprovalStatus
        orderId={orderId}
        orderStatus={order.status}
        showActions={false}
        items={items}
        statusBeforeCancel={order.status_before_cancel}
      />
    </div>
  );
}

export default OrderExpandedDetails;
